/* eslint-disable */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStaticTableData } from '../config/tableStaticConfig';
import { getStaticTableContextConfig } from '../utils/utils';
import { modes } from '@/config/multipleModalConfig';
import { analyticsAction } from '@/Actions';

const multiPoContext = createContext();

export const useMultiplePo = () => {
	const context = useContext(multiPoContext);
	if (!context) {
		throw new Error('useMultiPO must be used within MultiPOProvider');
	}
	return context;
};

export const MultiPoContextProvider = (props) => {
	const { initialState, info = {}, children } = props;
	const {
		editMode = false,
		viewMode = false,
		selectedPOs = [],
		ppId = null,
		navSelectedStore = null,
		mode: modeName = 'order',
		initialTableData = null,
		initialFieldsData = null,
	} = info;
	const productList = useSelector(
		(state) => state?.form?.ALL_PRODUCTS?.data?.LIST
	);
	const batches = useSelector((state) => state?.analytics?.INGREDIENT_LIST);
	const initialStateRef = useRef(initialState);
	const initialStateSet = useRef(false);
	const apiDispatch = useDispatch();

	const reducer = (state, action) => {
		let newState;
		switch (action.type) {
			case 'SET_INITIAL_STATE':
				newState = {
					...action.payload,
				};
				break;
			case 'SET_STATE':
				newState = {
					...state,
					...action.payload,
				};
				break;
			case 'RESET':
				newState = {
					...initialStateRef.current,
				};
				break;
			case 'REMOVE':
				newState = {};
				break;
			default:
				return state;
		}
		return newState || state;
	};

	const [state, dispatch] = useReducer(reducer, initialState);
	const [tableData, setTableData] = useState({
		headers: [],
		rows: [],
	});
	const [prevTableData, setPrevTableData] = useState({});
	const [lists, setLists] = useState({});
	const [loading, setLoading] = useState(false);
	const [editingRowIndex, setEditingRowIndex] = useState(null);
	const [workerAllocations, setWorkerAllocations] = useState({});
	const [searchQuery, setSearchQuery] = useState('');
	const [sortColumn, setSortColumn] = useState(null);
	const [sortType, setSortType] = useState(null);
	const originalRowsRef = useRef(null);
	const changeQtyRef = useRef(true);
	const storeList = useRef([]);
	const lastProcessedRef = useRef({ trigger: null, batches: null });

	useEffect(() => {
		initialStateRef.current = initialState;
		if (!editMode && !viewMode) {
			dispatch({ type: 'SET_INITIAL_STATE', payload: initialState });
		}
	}, [initialState]);

	const modeConfig = modes[modeName];

	const {
		trigger: currentTrigger,
		fetchIngredients: shouldFetchIngredients,
	} = getStaticTableContextConfig(modeName, state);

	useEffect(() => {
		if (
			modeConfig?.tableDataStatic &&
			shouldFetchIngredients &&
			!editMode &&
			!viewMode &&
			state?.currentStep === 1 &&
			currentTrigger
		) {
			apiDispatch(analyticsAction.getIngredients(currentTrigger));
		}
	}, [
		modeConfig?.tableDataStatic,
		shouldFetchIngredients,
		editMode,
		viewMode,
		state?.currentStep,
		currentTrigger,
	]);

	useEffect(() => {
		const currentTriggerStr = JSON.stringify(currentTrigger);
		if (
			modeConfig?.tableDataStatic &&
			productList?.length > 0 &&
			!editMode &&
			!viewMode &&
			(state?.currentStep === 1 || !state?.currentStep)
		) {
			const isTriggerChanged =
				lastProcessedRef.current.trigger !== currentTriggerStr;
			const isBatchesChanged =
				lastProcessedRef.current.batches !== batches;

			if (
				isTriggerChanged ||
				(isBatchesChanged &&
					(tableData.rows.length === 0 ||
						lastProcessedRef.current.trigger === currentTriggerStr))
			) {
				const { headers, rows, apiStoreList } = getStaticTableData(
					modeName,
					modeConfig,
					productList,
					batches,
					state,
					lists,
					isTriggerChanged ? { rows: [], headers: [] } : tableData
				);
				setTableData({ headers, rows });
				storeList.current = apiStoreList;
				lastProcessedRef.current = { trigger: currentTriggerStr, batches };
			}
		}
	}, [
		modeName,
		modeConfig?.tableDataStatic,
		productList,
		batches,
		editMode,
		viewMode,
		state?.currentStep,
		currentTrigger,
		lists,
	]);

	useEffect(() => {
		if (!editMode && !viewMode) {
			const updates = {};
			let hasUpdate = false;
			modeConfig?.fields?.forEach((field) => {
				const list = lists[field.dataKey];
				if (list?.length > 0) {
					if (['shift'].includes(field.id)) {
						updates[field.id] = list[0].value || list[0].id;
						hasUpdate = true;
					}
				}
			});

			if (hasUpdate) {
				dispatch({ type: 'SET_STATE', payload: updates });
			}
		}
	}, [lists, editMode, viewMode, modeConfig]);

	const refreshIngredients = useCallback(() => {
		if (currentTrigger) {
			apiDispatch(analyticsAction.getIngredients(currentTrigger));
			lastProcessedRef.current = { trigger: null, batches: null };
		}
	}, [currentTrigger, apiDispatch]);

	const getProductData = useMemo(() => {
		const data = [];
		productList?.forEach((product) => {
			if (state?.orderType === 'PO' && product?.skuType === 'INGREDIENT') {
				return;
			}
			product?.variants?.forEach((variant) => {
				data.push({
					viewOnlyLabel: `${product?.productName}(${variant?.displayName})`,
					label: `${product?.productName}(${variant?.displayName})${variant?.clientVariantId
						? variant?.clientVariantId
						: ' '
						}`,
					value: variant?.id,
					variantId: variant?.id,
					itemId: variant?.id,
					clientVariantId: variant?.clientVariantId,
					productId: product?.id,
					unit: product?.unit,
					skuType: product?.skuType,
				});
			});
		});
		return data;
	}, [productList, state]);

	const value = {
		state,
		dispatch,
		editingRowIndex,
		setEditingRowIndex,
		tableData,
		setTableData,
		changeQtyRef,
		storeList,
		productList,
		getProductData,
		lists,
		setLists,
		loading,
		setLoading,
		editMode,
		viewMode,
		selectedPOs,
		ppId,
		navSelectedStore,
		prevTableData,
		setPrevTableData,
		initialTableData,
		initialFieldsData,
		initialStateSet,
		batches,
		searchQuery,
		setSearchQuery,
		originalRowsRef,
		refreshIngredients,
		workerAllocations,
		setWorkerAllocations,
		sortColumn,
		setSortColumn,
		sortType,
		setSortType,
		orderwise: info?.orderwise,
	};
	return (
		<multiPoContext.Provider value={value}>
			{children}
		</multiPoContext.Provider>
	);
};
